import type { Key } from "react-stately";

import { Outlet, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Button } from "@/components/button";
import { PlusIcon } from "@/components/icons";
import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";
import { BrandFinishCreation } from "@/features/brands/create/sessions/brandFinishCreation";
import { useDeleteBrand } from "@/features/brands/shared/hooks/useDeleteBrand";
import { useDismissBrandReview } from "@/features/brands/shared/hooks/useDismissBrandReview";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";
import { ProjectInfoPopup } from "@/features/shared/components/sticky-widget/account-information";
import { usePageTitle } from "@/hooks/usePageTitle";

import { BrandAiAssetsToggle } from "./components/BrandAiAssetsToggle";
import { BrandHeader } from "./components/BrandHeader";
import { BrandIconUploadModal } from "./components/BrandIconUploadModal";
import { DeleteBrandButton } from "./components/DeleteBrandButton";
import { NewNoteModal } from "./components/knowledge/components/modal/NewNoteModal";
import { KnowledgeActionsContext } from "./components/knowledge/KnowledgeActionsContext";
import { ReviewBrandDetailsModal } from "./components/ReviewBrandDetailsModal";
import { TabContentWrapper } from "./components/TabContentWrapper";
import { useStableImageUrl } from "./hooks/useStableImageUrl";
import { BRAND_EDIT_TABS, TAB_CONFIG } from "./tab-config";

interface BrandEditPageProps {
  readonly brandId: string;
}

export function BrandEditPage({ brandId }: BrandEditPageProps) {
  const brandIdNum = Number(brandId);
  const { data: brandData } = useGetBrandById(brandIdNum, {
    pollWhileExtracting: true,
  });
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const deleteBrand = useDeleteBrand();
  const dismissReview = useDismissBrandReview();

  const isPreparing =
    brandData !== undefined &&
    !!brandData.analysisStatus &&
    brandData.analysisStatus !== "completed" &&
    brandData.analysisStatus !== "failed";

  const getCurrentTab = (): string => {
    if (matchRoute({ to: "/brands/$brandId/overview" }))
      return BRAND_EDIT_TABS.BRAND_OVERVIEW;
    if (matchRoute({ to: "/brands/$brandId/knowledge" }))
      return BRAND_EDIT_TABS.KNOWLEDGE;
    if (matchRoute({ to: "/brands/$brandId/visualdirection" }))
      return BRAND_EDIT_TABS.INSPIRATION_BOARD;
    if (matchRoute({ to: "/brands/$brandId/requests" }))
      return BRAND_EDIT_TABS.REQUESTS;

    return BRAND_EDIT_TABS.BRAND_OVERVIEW;
  };

  const selectedTab = getCurrentTab();

  const handleCloseModal = useCallback(() => {
    dismissReview.mutate(brandIdNum);
  }, [dismissReview, brandIdNum]);
  const [isIconModalOpen, setIsIconModalOpen] = useState(false);
  const [isNewNoteModalOpen, setIsNewNoteModalOpen] = useState(false);
  const openNewNoteModal = useCallback(() => setIsNewNoteModalOpen(true), []);
  const closeNewNoteModal = useCallback(() => setIsNewNoteModalOpen(false), []);
  const stableProfilePicture = useStableImageUrl(
    brandData?.profilePicture ?? undefined,
  );

  const brandTab = TAB_CONFIG.find((t) => t.id === selectedTab);

  usePageTitle(`Brands - ${brandTab?.label ?? "Brand"}`);

  const handleTabChange = useCallback(
    (key: Key) => {
      navigate({
        params: { brandId },
        to: `/brands/$brandId/${key.toString()}`,
      });
    },
    [brandId, navigate],
  );

  if (isPreparing) {
    return <BrandFinishCreation />;
  }

  const brandName = brandData?.name ?? "Brand";

  return (
    <KnowledgeActionsContext.Provider value={{ openNewNoteModal }}>
      <ProjectInfoPopup projectId={brandData?.project?.id} />
      <ReviewBrandDetailsModal
        isOpen={brandData?.needsReview ?? false}
        onClose={handleCloseModal}
      />
      <BrandIconUploadModal
        brandId={brandIdNum}
        currentIconUrl={stableProfilePicture}
        isOpen={isIconModalOpen}
        onClose={() => setIsIconModalOpen(false)}
      />
      <NewNoteModal
        brandId={brandIdNum}
        isOpen={isNewNoteModalOpen}
        onClose={closeNewNoteModal}
      />
      <div className="relative bg-bg-tertiary p-md pb-5xl sm:p-2xl">
        <div className="mx-auto flex w-full flex-col">
          <div className="flex items-center justify-between bg-bg-primary p-md rounded-t-xl">
            <BrandHeader
              brandName={brandName}
              isNew={brandData?.isNew ?? false}
              onEditIcon={() => setIsIconModalOpen(true)}
              profilePicture={stableProfilePicture}
            />
            {selectedTab === BRAND_EDIT_TABS.KNOWLEDGE && (
              <Button className="mr-4" onPress={openNewNoteModal}>
                <PlusIcon /> New note
              </Button>
            )}
            {selectedTab === BRAND_EDIT_TABS.BRAND_OVERVIEW && (
              <BrandAiAssetsToggle
                brandId={brandIdNum}
                restrictAiAssets={brandData?.restrictAiAssets ?? false}
              />
            )}
          </div>
          <Tabs
            className="rounded-b-xl bg-bg-primary"
            onSelectionChange={handleTabChange}
            selectedKey={selectedTab}
          >
            <div className="overflow-x-auto scrollbar-none sm:overflow-x-visible">
              <TabList
                aria-label="Brand Management"
                className="flex min-w-full flex-nowrap px-2xl sm:flex-wrap"
              >
                {TAB_CONFIG.map((tab) => (
                  <Tab
                    className={({ isSelected }) =>
                      `py-3 text-base  ${isSelected ? "sm:rounded-t-2xl sm:bg-brand-edit-tab-active-bg [&_svg]:text-brand-edit-tab-active-icon after:left-0 after:right-0 after:h-[3px]" : ""}`
                    }
                    id={tab.id}
                    key={tab.id}
                  >
                    <span className="flex items-center gap-xs whitespace-nowrap">
                      <span className="hidden sm:inline">{tab.icon}</span>
                      {tab.label}
                    </span>
                  </Tab>
                ))}
              </TabList>
            </div>

            {TAB_CONFIG.map((tab) => (
              <TabPanel className="flex-initial pb-0" id={tab.id} key={tab.id}>
                {selectedTab === tab.id && (
                  <TabContentWrapper>
                    <Outlet />
                  </TabContentWrapper>
                )}
              </TabPanel>
            ))}
          </Tabs>
          <DeleteBrandButton
            brandName={brandName}
            isDeleting={deleteBrand.isPending}
            onDelete={() =>
              deleteBrand.mutate(brandIdNum, {
                onSuccess: () => navigate({ to: "/brands" }),
              })
            }
          />
        </div>
      </div>
    </KnowledgeActionsContext.Provider>
  );
}
